import { useEffect, useRef, useState } from "react";
import { InfoFooter, InfoHeader } from "../components/InfoLayout";
import SlCloudShell from "../brands/servicelad/SlCloudShell";
import { RelatedFreeTools, ToolFaq, toolPageSchemas } from "../components/FreeToolSeo";
import { packagesFor } from "../config/pricing";
import usePageMeta from "../hooks/usePageMeta";
import infoCss from "../styles/info.css?raw";
import taxCss from "../styles/tax-calculator.css?raw";
import { formatUkReg, isValidUkReg, normalizeUkReg } from "../../lib/vehicle/regValidation";

const officialRatesUrl = "https://www.gov.uk/government/publications/rates-of-vehicle-tax-v149";

const brandCopy = {
  serviceboss: {
    eyebrow: "Free car tax check",
    title: "How much is my car tax?",
    intro: "Enter your reg to see how much car tax or road tax you may pay this year.",
    metaTitle: "How Much Is My Car Tax? Free Calculator | ServiceBoss",
    metaDescription: "How much is my car tax? Enter a UK registration for free with ServiceBoss. See yearly and six-month road tax costs using 2026 rates.",
    guideTitle: "See the cost before you buy",
    guideCopy: "ServiceBoss checks the vehicle and shows how much car tax may cost and how you can pay.",
    softTitle: "Need the wider vehicle record?",
    softCopy: "Check the current tax and MOT record together for free.",
    softLabel: "Run the free vehicle check",
    softHref: "/vehicle-check",
    bridgeKicker: "Before you buy",
    bridgeTitle: "Tax shows the running cost. History shows the risk.",
    bridgeCopy: "A road tax figure cannot tell you whether a car has outstanding finance, a write-off marker or a stolen record.",
  },
  servicebyreg: {
    eyebrow: "Free road tax check",
    title: "How much road tax will I pay?",
    intro: "Enter the UK registration plate. We find the vehicle and show the road tax price. You do not type make, model or CO2.",
    metaTitle: "How Much Road Tax Will I Pay? | ServiceByReg",
    metaDescription: "Find out how much road tax you will pay from a UK registration plate. No make or model form. Free 12-month and six-month figures on ServiceByReg.",
    guideTitle: "Why the plate is enough",
    guideCopy: "ServiceByReg reads the vehicle record from the registration, then matches it to current government rates. You skip the rate tables and the long vehicle form.",
    buttonLabel: "Check road tax",
    softTitle: "Buying a used car?",
    softCopy: "Open the free tax and MOT record for the same plate in one place.",
    softLabel: "Open tax and MOT check",
    softHref: "/vehicle-check",
    bridgeKicker: "Before you leave a deposit",
    bridgeTitle: "Road tax is only the running cost",
    bridgeCopy: "A cheap tax bill does not mean the car is clean to buy. It will not show finance, theft, write-offs or odd mileage.",
    guideHref: "/guides/how-much-is-my-car-tax",
    guideLabel: "How UK road tax is set",
    scopeTitle: "Vehicles this road tax check can price",
    pathLabel: "Road tax check",
    faqTitle: "Road tax questions",
    directKicker: "Quick answer",
    directTitle: "What does road tax cost each year?",
    directCopy: "From 1 April 2026, most cars first taxed after 1 April 2017 move onto a £200 standard yearly rate after the first licence. Older cars often follow CO2 or engine size rules instead. Put the plate in above and we apply the DVLA V149 rates for 2026 to 2027, including six-month options when they exist.",
    steps: [
      ["Type the plate", "We check it looks like a normal UK registration."],
      ["We read the vehicle file", "Age, fuel, engine size and emissions point to the right band."],
      ["Get the prices", "See 12-month cost, plus six-month where allowed."],
    ],
    scopeItems: [
      "Cars taxed on older rules",
      "Cars priced from CO2 figures",
      "Post-2017 cars and EVs",
      "Bikes, trikes and many vans",
    ],
    scopeNote: "Figures follow the government rates that start on 1 April 2026 and change when those rates change.",
    disclaimer: "This figure is an estimate only. DVLA may charge a slightly different amount if extra details sit outside the free plate lookup. Confirm the total with DVLA before you pay.",
    alternateName: [
      "How much road tax will I pay",
      "Road tax check UK registration",
      "UK road tax from number plate",
      "Yearly road tax cost",
    ],
    navLabel: "Road tax check",
  },
  servicelad: {
    eyebrow: "Plate lookup for road tax",
    title: "What will my road tax cost?",
    intro: "Drop in the registration. ServiceLad looks up the car and returns the road tax amount. No make and model fields to fill.",
    metaTitle: "What Will My Road Tax Cost? | ServiceLad",
    metaDescription: "See what your road tax will cost with a UK plate only. Skip make and model. Free yearly and half-year estimates on ServiceLad.",
    guideTitle: "Built around the registration",
    guideCopy: "You give ServiceLad the plate. We pull the vehicle details and turn them into a clear road tax price, with a plain note if anything is missing.",
    buttonLabel: "Show my road tax",
    softTitle: "Want tax and MOT together?",
    softCopy: "Run the free combined tax and MOT look-up on the same registration.",
    softLabel: "Run tax and MOT look-up",
    softHref: "/vehicle-check",
    bridgeKicker: "Before you hand over money",
    bridgeTitle: "Price the tax, then check the car",
    bridgeCopy: "Knowing the road tax helps the budget. It still will not uncover finance markers, stolen flags, write-offs or mileage gaps.",
    guideHref: "/guides/dvla-vehicle-enquiry-explained",
    guideLabel: "What DVLA shows for free",
    scopeTitle: "What you can look up here",
    pathLabel: "Check road tax",
    faqTitle: "Questions about road tax cost",
    directKicker: "In plain terms",
    directTitle: "How much should I budget for road tax?",
    directCopy: "For the 2026/27 year, cars that joined the system after 1 April 2017 usually settle on a £200 a year standard rate once the first licence ends. Older motors may sit on emissions or cc bands instead. Enter the plate above and we line it up with DVLA V149 rates from April 2026, then show yearly and six-month choices when DVLA offers them.",
    steps: [
      ["Add the registration", "We confirm it matches a UK plate pattern."],
      ["Match the car record", "Fuel, age, engine and emissions decide the band."],
      ["Read the amounts", "Yearly first, then shorter payment options if available."],
    ],
    scopeItems: [
      "Pre-emissions era cars",
      "Emissions-based cars",
      "Newer petrol, diesel and electric cars",
      "Motorbikes, trikes and light vans",
    ],
    scopeNote: "Amounts use official rates from 1 April 2026. We refresh them when the published rates move.",
    disclaimer: "Treat this as a guide price. A free registration search can miss a detail that changes the final DVLA total by a few pence. Always double-check with DVLA before paying.",
    alternateName: [
      "What will my road tax cost",
      "Check road tax from plate",
      "Road tax amount UK",
      "Estimate my vehicle tax",
    ],
    navLabel: "Check road tax",
  },
};


const taxFaqs = [
  ["How much is my car tax?", "Enter your UK registration above. We look up the vehicle with DVLA and match it to the current government rates, then show yearly and six-month prices when they apply."],
  ["How much will my road tax be?", "Car tax and road tax are the same charge. Enter the reg and you get the estimated cost for 12 months, plus six-month options when DVLA allows them."],
  ["How much is my car tax by registration?", "Yes, this check works from the reg alone. You do not need the make, model or CO2 figure first. We pull those from the vehicle record."],
  ["Can I check motorbike tax as well as cars?", "Yes. This check covers cars, motorbikes, trikes and many vans, using the same government rate schedules that apply to that vehicle type."],
  ["Is car tax the same as road tax?", "Yes. People say car tax, road tax or vehicle tax, but they all mean Vehicle Excise Duty, the charge DVLA collects to keep a vehicle taxed for the road."],
  ["Does this use the latest UK rates?", "Yes. Prices follow the government rates from 1 April 2026. We update them when the official rates change."],
];

const taxFaqsByBrand = {
  servicebyreg: [
    ["How much road tax will I pay?", "Enter the UK registration above. We look up the vehicle and show the tax price. You do not need make, model or CO2."],
    ["Do I have to enter the car details?", "No. The plate is enough. We take the vehicle details from the record, then match current government rates."],
    ["Is road tax the same as car tax?", "Yes. Road tax, car tax and vehicle tax all mean Vehicle Excise Duty, the charge DVLA collects to keep a vehicle taxed."],
    ["Can I price a motorbike too?", "Yes. Cars, motorbikes, trikes and many vans are covered, using the rate schedule that fits that vehicle."],
    ["Are the figures up to date?", "Yes. They follow the government rates from 1 April 2026 and change when those rates change."],
  ],
  servicelad: [
    ["What will my road tax cost?", "Put the registration in the box. We fetch the car record and return the road tax amount without a make and model form."],
    ["Why only the registration?", "DVLA already stores the facts that set the band. The plate unlocks them, so you skip typing engine size or CO2 yourself."],
    ["Car tax or road tax: which is it?", "Same thing. People use both names for Vehicle Excise Duty, the yearly charge to keep a vehicle taxed for the road."],
    ["Does this work for bikes and vans?", "Yes. Motorbikes, trikes and many vans are included, priced on the same official schedules for that type."],
    ["When do the rates refresh?", "We use the rates published for 1 April 2026 and update this page when the official schedule changes."],
  ],
};

function formatMoney(value) {
  if (value == null) return "Not available";
  const hasPence = Math.round(value * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: hasPence ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatMonth(value) {
  if (!value) return "Not available";
  const [year, month] = value.split("-").map(Number);
  if (!year || !month) return value;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

function Detail({ label, value }) {
  return <div className="tax-detail"><span>{label}</span><strong>{value ?? "Not available"}</strong></div>;
}

function TaxResult({ data }) {
  const { vehicle, tax } = data;
  const possibleRates = [...new Set(tax.possibleRates || [])].sort((a, b) => a - b);
  const normalizedStatus = String(vehicle.taxStatus || "").trim().toUpperCase();
  const statusClass = normalizedStatus === "TAXED" ? " is-taxed" : normalizedStatus === "UNTAXED" ? " is-untaxed" : normalizedStatus === "SORN" ? " is-sorn" : "";
  const normalizedMot = String(vehicle.motStatus || "").trim().toUpperCase();
  const motClass = normalizedMot === "VALID" ? " is-mot-valid" : normalizedMot === "EXPIRED" || normalizedMot === "NOT VALID" ? " is-mot-expired" : "";

  return (
    <section className="tax-result" aria-live="polite">
      <div className="tax-result-head">
        <div>
          <span className="tax-result-kicker">Vehicle found</span>
          <h2>{vehicle.make || "Vehicle"}</h2>
          <span className="tax-status-row">
            <span className={`tax-status${statusClass}`}>{vehicle.taxStatus || "Tax status not shown"}</span>
            {vehicle.motStatus && <span className={`tax-status tax-mot${motClass}`}>{normalizedMot === "VALID" ? "MOT valid" : normalizedMot === "EXPIRED" || normalizedMot === "NOT VALID" ? "MOT expired" : vehicle.motStatus}</span>}
          </span>
        </div>
        <div className="tax-result-plate"><span>UK</span><strong>{formatUkReg(vehicle.registration)}</strong></div>
      </div>

      {tax.exact ? (
        <>
          <div className="tax-rate-label">{tax.label}</div>
          <div className="tax-rate-grid">
            <article className="tax-rate-card is-primary"><span>Pay for 12 months</span><strong>{formatMoney(tax.annual)}</strong><small>One payment</small></article>
            {tax.sixMonth != null && <article className="tax-rate-card"><span>Pay for 6 months</span><strong>{formatMoney(tax.sixMonth)}</strong><small>{tax.directDebitSixMonth != null ? `${formatMoney(tax.directDebitSixMonth)} by Direct Debit` : "One payment"}</small></article>}
          </div>
        </>
      ) : (
        <div className="tax-unknown">
          <span>Exact price not available</span>
          <h3>We need a little more vehicle information.</h3>
          <p>{tax.reason}</p>
          {possibleRates.length > 0 && <div className="tax-possible">Possible yearly prices: {possibleRates.map(formatMoney).join(" or ")}</div>}
        </div>
      )}

      {tax.standardRate && (
        <div className="tax-standard-note"><span>After the first year</span><strong>{formatMoney(tax.standardRate.annual)} a year</strong><small>Before any extra charge for an expensive car</small></div>
      )}

      {tax.supplement && (
        <div className="tax-warning"><strong>Extra charge for expensive cars</strong><span>{tax.supplement.message} We cannot add it because the car's price when new is not included in the free check.</span></div>
      )}

      <div className="tax-detail-grid">
        {tax.band && <Detail label="Tax band" value={`Band ${tax.band}`} />}
        <Detail label="Fuel" value={vehicle.fuelType} />
        <Detail label="First registered" value={formatMonth(vehicle.monthOfFirstRegistration)} />
        <Detail label="Emissions" value={vehicle.co2Emissions != null ? `${vehicle.co2Emissions} g/km` : null} />
        <Detail label="Engine size" value={vehicle.engineCapacity ? `${vehicle.engineCapacity} cc` : null} />
      </div>
    </section>
  );
}

export default function TaxCalculatorPage({ brand }) {
  const riskPlan = packagesFor(brand.key)[0];
  const copy = brandCopy[brand.key] || brandCopy.serviceboss;
  const initialRegistration = normalizeUkReg(new URLSearchParams(window.location.search).get("reg"));
  const [registration, setRegistration] = useState(initialRegistration);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const autoLookupDone = useRef(false);
  const canonical = `https://${brand.domain}/car-tax-calculator/`;

  usePageMeta({ title: copy.metaTitle, description: copy.metaDescription, canonical });

  const runLookup = async (value) => {
    const clean = normalizeUkReg(value);
    if (!isValidUkReg(clean)) {
      setError("Check the reg and try again.");
      setData(null);
      return;
    }

    setLoading(true);
    setError("");
    setData(null);
    try {
      const response = await fetch(`/api/tax-check?reg=${encodeURIComponent(clean)}`, { headers: { Accept: "application/json" } });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "The vehicle could not be checked.");
      setData(result);
      const resultPath = result.page_url
        ? new URL(result.page_url).pathname
        : `/car-tax-calculator/?reg=${encodeURIComponent(clean)}`;
      window.history.replaceState(null, "", resultPath);
    } catch (lookupError) {
      setError(lookupError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoLookupDone.current && isValidUkReg(initialRegistration)) {
      autoLookupDone.current = true;
      runLookup(initialRegistration);
    }
  }, []);

  const byRegBrand = brand.key === "servicebyreg" || brand.key === "servicelad";
  const faqItems = taxFaqsByBrand[brand.key] || taxFaqs;
  const schema = {
    "@context": "https://schema.org",
    "@graph": toolPageSchemas({
      brand,
      canonical,
      name: (copy.metaTitle || "").split(" | ")[0],
      description: copy.metaDescription,
      alternateName: copy.alternateName || [
        "How much is my car tax",
        "How much will my road tax be",
        "How much is my road tax",
        "Check my car tax cost",
        "How much car tax by reg",
        "How much is tax on my car",
        "Car tax calculator",
        "Road tax calculator",
      ],
      pathLabel: copy.pathLabel || "Car tax calculator",
    }),
  };
  // With a checked reg the button goes to the Risk Check for it; without one it takes the
  // visitor back up to the reg box instead of promising "this vehicle".
  const hasReg = isValidUkReg(registration);
  const paidCheckHref = hasReg
    ? `/checkout?vrm=${encodeURIComponent(registration)}&plan=risk`
    : "#tax-registration";

  return (
    <SlCloudShell brand={brand}>
      <style>{infoCss}</style>
      <style>{taxCss}</style>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <InfoHeader brand={brand} />
      <main className="tax-main">
        <section className="tax-hero">
          <div className="wrap tax-hero-grid">
            <div className="tax-hero-copy">
              <span className="tax-eyebrow">{copy.eyebrow}</span>
              <h1>{copy.title}</h1>
              <p>{copy.intro}</p>
              <div className="tax-proof"><span>Free to use</span><span>Uses government rates</span><span>No payment needed</span></div>
            </div>

            <form className="tax-form-card" onSubmit={(event) => { event.preventDefault(); runLookup(registration); }} noValidate>
              <label htmlFor="tax-registration">Enter your reg</label>
              <div className="tax-plate-input">
                <span className="tax-plate-band" aria-hidden="true"><img src="/gb-flag.svg" alt="" />UK</span>
                <input
                  id="tax-registration"
                  value={registration}
                  maxLength="8"
                  autoComplete="off"
                  inputMode="text"
                  placeholder="ENTER REG"
                  aria-describedby="tax-form-help tax-form-error"
                  onChange={(event) => {
                    setRegistration(normalizeUkReg(event.target.value));
                    setError("");
                  }}
                />
              </div>
              <button type="submit" disabled={loading}>{loading ? "Checking your vehicle..." : (copy.buttonLabel || "Check my car tax")}</button>
              <small id="tax-form-help">We only use your reg to check the vehicle with DVLA.</small>
              <div className="tax-form-error" id="tax-form-error" role="alert">{error}</div>
            </form>
          </div>
        </section>

        <div className="wrap tax-page-body">
          {loading && <div className="tax-loading" aria-live="polite"><span /><p>Checking your vehicle...</p></div>}
          {data && <TaxResult data={data} />}

          <aside className="tax-soft-cta">
            <div>
              <strong>{copy.softTitle}</strong>
              <span>{copy.softCopy}</span>
            </div>
            <a href={copy.softHref}>{copy.softLabel}</a>
          </aside>

          <section className="tax-direct-answer">
            <span className="tax-section-kicker">{copy.directKicker || "Straight answer"}</span>
            <h2>{copy.directTitle || "How much is my car tax per year?"}</h2>
            <p>{copy.directCopy || "For the 2026 to 2027 tax year, cars registered from 1 April 2017 usually pay a £200 standard yearly rate after their first licence. Older cars may be priced by CO2 emissions or engine size. Enter the registration above and we use the vehicle details with the DVLA V149 rates from 1 April 2026 to show the 12-month price, plus six-month options where they are available."}</p>
            {copy.guideHref && <a className="tax-guide-link" href={copy.guideHref}>{copy.guideLabel}</a>}
          </section>

          <section className="tax-explainer">
            <div>
              <span className="tax-section-kicker">How it works</span>
              <h2>{copy.guideTitle}</h2>
              <p>{copy.guideCopy}</p>
            </div>
            <ol>
              {(copy.steps || [
                ["Enter your reg", "We check that it looks like a UK number plate."],
                ["We check the vehicle", "Its age, fuel, engine and emissions help us find the right price."],
                ["See the cost", "View yearly and six-month prices when available."],
              ]).map(([stepTitle, stepCopy], index) => (
                <li key={stepTitle}><span>{index + 1}</span><div><strong>{stepTitle}</strong><p>{stepCopy}</p></div></li>
              ))}
            </ol>
          </section>

          <section className="tax-scope">
            <div><img src="/svgs/taxprice.svg" alt="" /><h2>{copy.scopeTitle || "What the calculator covers"}</h2></div>
            <ul>
              {(copy.scopeItems || [
                "Older cars",
                "Cars priced by their emissions",
                "Newer cars and electric cars",
                "Motorbikes, trikes and vans",
              ]).map((item) => <li key={item}>{item}</li>)}
            </ul>
            <p>{copy.scopeNote || "These prices use the government rates starting on 1 April 2026. We will update them when the rates change."}{" "}<a href={officialRatesUrl} target="_blank" rel="noreferrer">See the government road tax rates</a>.</p>
          </section>

          <section className="tax-commercial-bridge">
            <div className="tax-bridge-copy">
              <span className="tax-section-kicker">{copy.bridgeKicker}</span>
              <h2>{copy.bridgeTitle}</h2>
              <p>{copy.bridgeCopy}</p>
            </div>
            <div className="tax-bridge-offer">
              <strong>{riskPlan.name}</strong>
              <ul>
                <li>Outstanding finance</li>
                <li>Stolen vehicle markers</li>
                <li>Insurance write-offs</li>
                <li>Recorded mileage checks</li>
              </ul>
              <div>
                <span>One payment. No subscription.</span>
                <b>£{riskPlan.plate}</b>
              </div>
              <a href={paidCheckHref} onClick={hasReg ? undefined : () => document.getElementById("tax-registration")?.focus()}>{hasReg ? "Check this vehicle" : "Enter a reg to check"}</a>
            </div>
          </section>

          <ToolFaq title={copy.faqTitle || "Common car tax questions"} items={faqItems} />
          <RelatedFreeTools current="tax" brandKey={brand.key} />
          <aside className="tax-disclaimer">
            <strong>This is an estimate.</strong>
            <span>{copy.disclaimer || "The final price can depend on details that are not included in a free registration check. It may differ by a few pence. Always check the final amount with DVLA before paying."}</span>
          </aside>
        </div>
      </main>
      <InfoFooter brand={brand} />
    </SlCloudShell>
  );
}
